import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../api/useApi';
import { productAPI } from '../../api/api';
import { getCurrentSeason, SEASON_THEMES } from '../../utils/seasonalCatalog';
import useIsMobile from '../../hooks/useIsMobile';
import FarmLifecycleLoader from '../common/FarmLifecycleLoader';
import { isSeasonalProduct } from '../../utils/productCatalog';

export default function SeasonalSnippet() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const season = getCurrentSeason();
  const theme = SEASON_THEMES[season];
  const { data, loading } = useApi(productAPI.getAll);

  const seasonalProducts = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return list.filter(isSeasonalProduct).slice(0, 1);
  }, [data]);

  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: isMobile ? '56px 16px' : '72px 24px',
        background: theme.atmosphere || theme.panel,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 18% 20%, rgba(255,255,255,0.55) 0, transparent 24%), radial-gradient(circle at 82% 24%, rgba(255,255,255,0.26) 0, transparent 18%), linear-gradient(135deg, rgba(255,255,255,0.18), transparent 45%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '-80px',
          left: '-40px',
          width: isMobile ? '160px' : '240px',
          height: isMobile ? '160px' : '240px',
          borderRadius: '50%',
          background: theme.orbOne,
          filter: 'blur(10px)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: '-30px',
          bottom: '-60px',
          width: isMobile ? '180px' : '260px',
          height: isMobile ? '180px' : '260px',
          borderRadius: '50%',
          background: theme.orbTwo,
          filter: 'blur(14px)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        <div
          style={{
            position: 'relative',
            borderRadius: '32px',
            overflow: 'hidden',
            background: theme.gradient,
            color: 'white',
            padding: isMobile ? '22px 18px' : '34px 34px 30px',
            boxShadow: `0 28px 60px ${theme.glow}`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(120deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 28%, transparent 56%), radial-gradient(circle at 82% 24%, rgba(255,255,255,0.16) 0, transparent 22%)',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.15fr) minmax(320px, 0.85fr)',
              gap: isMobile ? '22px' : '28px',
              alignItems: 'stretch',
            }}
          >
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '999px',
                  background: theme.chipBg,
                  color: theme.chipText,
                  padding: '8px 14px',
                  fontSize: '12px',
                  fontWeight: 800,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '16px',
                  backdropFilter: 'blur(10px)',
                }}
              >
                Sedyam's KisanKart View
              </div>

              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.3rem)', lineHeight: 1.05, fontWeight: 900, marginBottom: '14px' }}>
                {theme.title}
                <br />
                Seasonal Spotlight
              </h2>

              <p style={{ maxWidth: '620px', marginBottom: '22px', opacity: 0.94, lineHeight: 1.75, fontSize: isMobile ? '0.98rem' : '1.05rem' }}>
                A richer seasonal view of fruits and vegetables with more atmosphere, stronger mood, and region-aware discovery built into the experience.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '24px' }}>
                {['Season-first curation', 'Regional recommendations', 'Fresh local harvests'].map((pill) => (
                  <span
                    key={pill}
                    style={{
                      borderRadius: '999px',
                      padding: '9px 14px',
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      fontSize: '13px',
                      fontWeight: 700,
                    }}
                  >
                    {pill}
                  </span>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button
                  onClick={() => navigate('/ritu-roots')}
                  style={{
                    border: 'none',
                    borderRadius: '999px',
                    padding: '13px 24px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    color: theme.accent,
                    background: '#ffffff',
                    boxShadow: '0 16px 36px rgba(15, 23, 42, 0.18)',
                  }}
                >
                  Open KisanKart
                </button>
                {/* <button
                  onClick={() => navigate('/rice-store')}
                  style={{
                    borderRadius: '999px',
                    padding: '13px 24px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    color: 'white',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.22)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  Explore Fresh Picks
                </button> */}
              </div>
            </div>

            <div style={{ display: 'grid', gap: '14px', alignContent: 'start' }}>
              {loading ? (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: '24px',
                    overflow: 'hidden',
                  }}
                >
                  <FarmLifecycleLoader title="Loading fresh products" subtitle="Sorting farm produce and current inventory for the store..." />
                </div>
              ) : seasonalProducts.length === 0 ? (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    borderRadius: '24px',
                    padding: '24px',
                    border: '1px solid rgba(255,255,255,0.18)',
                  }}
                >
                  <p style={{ opacity: 0.9 }}>No seasonal products available right now.</p>
                </div>
              ) : (
                seasonalProducts.map((product) => (
                  <button
                    key={product._id}
                    onClick={() => navigate(`/products/${product._id}`)}
                    style={{
                      textAlign: 'left',
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '124px 1fr',
                      gap: '16px',
                      alignItems: 'center',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(255,255,255,0.18)',
                      color: 'white',
                      borderRadius: '26px',
                      padding: '14px',
                      cursor: 'pointer',
                      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.16)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div style={{ height: isMobile ? '180px' : '124px', borderRadius: '20px', overflow: 'hidden', background: 'rgba(255,255,255,0.14)' }}>
                      <img
                        src={product.images?.[0] || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=500'}
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div>
                      <div style={{ display: 'inline-flex', borderRadius: '999px', padding: '5px 10px', background: 'rgba(255,255,255,0.14)', fontSize: '11px', fontWeight: 800, marginBottom: '10px' }}>
                        {product.category}
                      </div>
                      <p style={{ fontSize: '1.2rem', fontWeight: 900, marginBottom: '6px' }}>{product.name}</p>
                      <p style={{ fontSize: '13px', opacity: 0.88, marginBottom: '8px' }}>
                        Rs.{product.pricePerKg}/kg
                      </p>
                      <p style={{ fontSize: '12px', opacity: 0.75, marginBottom: '12px' }}>
                        One fresh sample pick from this season. Open KisanKart to browse the full collection.
                      </p>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '9px 14px',
                          borderRadius: '999px',
                          background: 'rgba(255,255,255,0.16)',
                          border: '1px solid rgba(255,255,255,0.22)',
                          fontSize: '12px',
                          fontWeight: 800,
                        }}
                      >
                        View seasonal collection
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
